import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { fetchDataDetailOptions, resetActiveLocation } from '@actions/data-detail-actions';
import {
  loadBatch,
  geocodeBatchRows,
  fetchMissingEntityFields,
  setBatchLoading,
  setBatchLoadingProgress
} from '@actions/batch-actions';
import { getEntityType } from '@constants/config';

import Drawer from '@components/batch/batch-upload-edit/drawer';
import Content from '@components/batch/batch-upload-edit/content';
import BatchContext from '@components/batch/batch-upload-edit/batch-context';
import LoadingDialog from '@components/batch/batch-upload-edit/loading-dialog';
import UploadBatchDialog from '@components/batch/batch-upload-edit/upload-batch-dialog';
import TopBar from '@components/details/top-bar';

import {
  convertCSVDataForContext,
  convertCSVErrorsForContext,
  getBatch
} from '@utils/batch-utils';

import './batch-upload-edit.scss';

const BatchUploadEdit = () => {
  const dispatch = useDispatch();
  const { id: fileId, rowId } = useParams();
  const [filter, setFilter] = useState('all');
  const [isInitialized, setIsInitialized] = useState(false);
  const batch = useMemo(() => getBatch(fileId), [fileId]);
  const batchEditState = useSelector(state => state.batchEdit[fileId]);
  const {
    map_category_type: categoryTypes,
    map_category: categories,
    segment_impact_type: segmentImpactTypes
  } = useSelector(state => state.dataTypes);

  // Coordinated loading function that fetches entity data and geocodes locations
  const runBatchDataLoading = useCallback(async (transformedRows, entityConfig) => {
    // Calculate total work to be done for progress tracking
    const rowsToGeocode = transformedRows.filter(row => row.data.segments?.some(segment => !segment.shape));
    // Rows that have an ID or external_id can be fetched from backend
    const rowsToFetch = transformedRows.filter(row => row.data.id || row.data.external_id);
    const totalWork = rowsToGeocode.length + rowsToFetch.length;

    // Set loading state and initial progress
    dispatch(setBatchLoading(fileId, true));
    dispatch(setBatchLoadingProgress(fileId, { geocoding: 0, entityFields: 0, total: totalWork }));

    try {
      // Run both geocoding and entity field fetching in parallel
      // Both functions now return promises and update progress incrementally
      await Promise.all([
        dispatch(geocodeBatchRows(fileId, transformedRows, entityConfig)),
        dispatch(fetchMissingEntityFields(fileId, transformedRows, batch.entity, categoryTypes))
      ]);
    } finally {
      // Clear loading state when done
      dispatch(setBatchLoading(fileId, false));
    }
  }, [batch?.entity, categoryTypes, dispatch, fileId]);

  useEffect(() => {
    if (batch && batch.rows && batch.entity && !isInitialized) {
      setIsInitialized(true);

      // Load data in the store from CSV.
      const transformedRows = batch.rows.map(row => ({
        id: row.id,
        display_row_nums: row.display_row_nums,
        data: convertCSVDataForContext(row, batch.entity, categoryTypes, categories, segmentImpactTypes),
        errors: convertCSVErrorsForContext(row, batch.errors || {})
      }));

      const entityType = getEntityType(batch.entity);
      const entityConfig = entityType?.config;

      dispatch(loadBatch({ fileId, rows: transformedRows, fileName: batch?.file?.path, entityType: batch.entity }));

      // Wait for OPTIONS call to complete before fetching entity data
      // This ensures we have the metadata for proper date/field decoding
      dispatch(fetchDataDetailOptions(batch.entity)).then(() => {
        // Run coordinated loading for geocoding and entity fields
        runBatchDataLoading(transformedRows, entityConfig);
      });
    }
  }, [batch, categoryTypes, categories, dispatch, fileId, segmentImpactTypes, isInitialized, runBatchDataLoading]);

  useEffect(() => {
    // If rowId changes, reset the active location:
    dispatch(resetActiveLocation());
  }, [dispatch, rowId]);

  const rowIndex = useMemo(() => {
    return batch?.rows?.findIndex(item => item.id === rowId);
  }, [rowId, batch?.rows]);

  const batchContextValue = {
    batch,
    fileId,
    filter,
    rowId,
    rowIndex,
    setFilter
  };

  const isLoading = batchEditState?.loading ?? false;
  const loadingProgress = batchEditState?.loadingProgress ?? null;

  return (
    <BatchContext.Provider value={batchContextValue}>
      <div styleName="batch-upload-edit-container">
        <TopBar />
        <div styleName="batch-upload-edit-content">
          <Drawer />
          <Content />
        </div>
        <UploadBatchDialog />
        <LoadingDialog open={isLoading} progress={loadingProgress} />
      </div>
    </BatchContext.Provider>
  );
};

export default memo(BatchUploadEdit);
